import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../libs/common/i18n';
import { getThemeColors } from '../libs/common/themeConfig';
import {
  ArrowPathIcon,
  ArrowRightCircleIcon,
  CursorArrowRaysIcon,
  EyeIcon,
  LockClosedIcon,
  QuestionMarkCircleIcon,
  TableCellsIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

/**
 * How to Use popup component
 */
export const HowToUse = ({ renderTrigger, open, onOpenChange } = {}) => {
  const { t } = useTranslation();
  const themeColors = getThemeColors();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;
  const guideItems = [
    {
      icon: CursorArrowRaysIcon,
      title: t('howToUsePickTitle'),
      body: t('howToUseStep1'),
    },
    {
      icon: ArrowPathIcon,
      title: t('howToUseSimTitle'),
      body: t('howToUseStep2'),
    },
    {
      icon: ArrowRightCircleIcon,
      title: t('howToUseAdvanceTitle'),
      body: t('howToUseStep3'),
    },
    {
      icon: EyeIcon,
      title: t('howToUseViewsTitle'),
      body: t('howToUseViewsBody'),
    },
    {
      icon: LockClosedIcon,
      title: t('howToUseLocksTitle'),
      body: t('howToUseLocksBody'),
    },
    {
      icon: TableCellsIcon,
      title: t('howToUseStandingsTitle'),
      body: t('howToUseStandingsBody'),
    },
  ];

  // Handle ESC key to close modal
  React.useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {renderTrigger ? renderTrigger({ open: () => setIsOpen(true), title: t('howToUse') }) : (
        <button
          onClick={() => setIsOpen(true)}
          className="p-2 rounded transition-colors min-h-[2.5rem] min-w-[2.5rem] touch-manipulation flex items-center justify-center hover:bg-[var(--hover-bg)]"
          style={{
            color: themeColors.text,
            backgroundColor: 'transparent',
            border: `1px solid ${themeColors.border}`,
            '--hover-bg': themeColors.hover,
          }}
          title={t('howToUse')}
        >
          <QuestionMarkCircleIcon style={{ width: '1.125rem', height: '1.125rem' }} />
        </button>
      )}

      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
          onClick={() => setIsOpen(false)}
        >
          <div
            style={{
              backgroundColor: themeColors.surface,
              borderRadius: '0.5rem',
              maxWidth: '42rem',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              textAlign: 'left',
            }}
            onClick={(e) => e.stopPropagation()}
          >
              {/* Header */}
              <div
                style={{
                  padding: '1.5rem',
                  borderBottom: `1px solid ${themeColors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <h2
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    margin: 0,
                    color: themeColors.text,
                    textAlign: 'left',
                  }}
                >
                  {t('howToUse')}
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded cursor-pointer transition-all flex items-center justify-center hover:bg-[var(--hover-bg)]"
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: themeColors.textSecondary,
                    '--hover-bg': themeColors.hover,
                  }}
                >
                  <XMarkIcon style={{ width: '1.5rem', height: '1.5rem' }} />
                </button>
              </div>

              {/* Content */}
              <div style={{ padding: '1.5rem', textAlign: 'left' }}>
                <p
                  style={{
                    margin: '0 0 1.25rem',
                    fontSize: '0.9375rem',
                    lineHeight: '1.6',
                    color: themeColors.textSecondary,
                    textAlign: 'left',
                  }}
                >
                  {t('howToUseIntro')}
                </p>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))',
                    gap: '0.75rem',
                  }}
                >
                  {guideItems.map(({ icon: Icon, title, body }, index) => (
                    <div
                      key={title}
                      style={{
                        display: 'flex',
                        gap: '0.75rem',
                        padding: '0.875rem',
                        border: `1px solid ${themeColors.border}`,
                        borderRadius: '0.5rem',
                        backgroundColor: index < 3 ? themeColors.hover : 'transparent',
                        textAlign: 'left',
                      }}
                    >
                      <div
                        style={{
                          flexShrink: 0,
                          width: '2rem',
                          height: '2rem',
                          borderRadius: '50%',
                          backgroundColor: index < 3 ? themeColors.accent : themeColors.surface,
                          color: index < 3 ? '#ffffff' : themeColors.accent,
                          border: index < 3 ? 'none' : `1px solid ${themeColors.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon style={{ width: '1.125rem', height: '1.125rem' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <h3
                          style={{
                            margin: '0 0 0.25rem',
                            fontSize: '0.9375rem',
                            lineHeight: '1.35',
                            fontWeight: 600,
                            color: themeColors.text,
                            textAlign: 'left',
                          }}
                        >
                          {title}
                        </h3>
                        <p
                          style={{
                            margin: 0,
                            fontSize: '0.875rem',
                            lineHeight: '1.5',
                            color: themeColors.textSecondary,
                            textAlign: 'left',
                          }}
                        >
                          {body}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: '1rem 1.5rem',
                  borderTop: `1px solid ${themeColors.border}`,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-6 py-2 text-sm font-medium rounded-lg cursor-pointer transition-opacity touch-manipulation hover:opacity-90"
                  style={{
                    backgroundColor: themeColors.accent,
                    color: '#ffffff',
                    border: 'none',
                  }}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>,
        document.body
      )}
    </>
  );
};
